// Adding a car — used by BOTH the superadmin panel and the cars team's own
// workspace, so a car is entered the same way whoever types it.
//
// The form asks a different second half depending on whose car it is, because
// the two deals genuinely need different facts. Asking for a purchase price on
// someone else's car is asking for a number that does not exist.

import React, { useEffect, useState } from 'react';
import { Alert, Button } from '../common/index.js';
import {
  CarPicker,
  ComboInput,
  Field,
  Modal,
  NumberInput,
  Select,
  VARIANTS,
  errText,
  inputCls,
} from './controls.jsx';
import { buildCarPayload, emptyCar, formFromCar } from './carForm.js';

const COMMISSION_TYPES = [
  { value: 'fixed', label: 'Fixed amount', hint: 'An agreed ₹, whatever it sells for' },
  { value: 'percent', label: '% of the sale price', hint: 'CAP keeps a share of whatever it fetches' },
  { value: 'margin', label: "Anything above the owner's price", hint: 'The owner always gets their number' },
];

const RC_OPTIONS = [
  { value: 'with_rc', label: 'With RC' },
  { value: 'without_rc', label: 'Without RC' },
];

const YEARS = Array.from({ length: 36 }, (_, i) => String(new Date().getFullYear() + 1 - i));

const AddCarModal = ({ open, onClose, catalog, onSaved, createFn, car = null, saveFn = null }) => {
  const editing = Boolean(car && saveFn);
  const [form, setForm] = useState(emptyCar);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) { setForm(editing ? formFromCar(car) : emptyCar()); setError(''); }
  }, [open, editing, car]);

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  const consignment = form.ownership === 'consignment';

  const submit = async () => {
    setError('');
    if (!form.brand.trim() || !form.model.trim()) {
      setError('Pick the car first — brand and model.');
      return;
    }
    setBusy(true);
    try {
      const payload = buildCarPayload(form, editing);
      const { data } = await (editing ? saveFn(payload) : createFn(payload));
      onSaved(data);
      onClose();
    } catch (e) {
      setError(errText(e, editing ? 'Could not save the changes.' : 'Could not add the car.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      wide
      title={editing ? 'Correct this car' : 'Add a car to the lot'}
      subtitle={editing
        ? 'Fix anything that was entered wrong. Clearing a box empties that detail.'
        : 'Whose car it is decides what CAP earns on it — pick that first.'}
    >
      {error && <Alert type="error" message={error} className="mb-4" />}

      <div className="space-y-5">
        {/* Whose car */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Whose car is it?" required>
            <Select
              value={form.ownership}
              onChange={(v) => setForm((f) => ({ ...f, ownership: v }))}
              options={[
                { value: 'own', label: 'Ours — CAP bought it', hint: 'Profit is the margin on the sale' },
                { value: 'consignment', label: "Someone else's", hint: 'CAP sells it and keeps a commission' },
              ]}
            />
          </Field>
          <Field label="Taken in on" required>
            <input type="date" value={form.acquired_on} onChange={(e) => set('acquired_on')(e.target.value)} className={inputCls} />
          </Field>
        </div>

        {/* The car */}
        <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
          <p className="mb-3 text-xs font-bold uppercase tracking-widest text-slate-400">The car</p>
          <Field label="Car" required hint="Type a few letters — the same catalogue the sales team picks from.">
            <CarPicker
              catalog={catalog}
              brand={form.brand}
              model={form.model}
              onPick={(brand, model) => setForm((f) => ({ ...f, brand, model }))}
            />
          </Field>

          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Field label="Variant">
              <Select value={form.variant} onChange={set('variant')} options={VARIANTS} />
            </Field>
            <Field label="Year">
              <ComboInput value={form.year} onChange={set('year')} options={YEARS} placeholder="2018" numeric />
            </Field>
            <Field label="Trim" hint="VXi, ZXi+ AMT…">
              <input type="text" value={form.trim} onChange={(e) => set('trim')(e.target.value)} placeholder="VXi" className={inputCls} />
            </Field>
          </div>

          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Field label="Registration number">
              <input
                type="text"
                value={form.registration_number}
                onChange={(e) => set('registration_number')(e.target.value.toUpperCase())}
                placeholder="TN37AB1234"
                className={inputCls}
              />
            </Field>
            <Field label="Colour">
              <input type="text" value={form.color} onChange={(e) => set('color')(e.target.value)} placeholder="White" className={inputCls} />
            </Field>
            <Field label="KM driven">
              <NumberInput value={form.km_driven} onChange={set('km_driven')} decimals={false} placeholder="48000" />
            </Field>
          </div>

          {/* Off the plate under the bonnet — the delivery note and the RC
              transfer both want these, so they are taken at the door. */}
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Engine number" hint="As on the engine plate">
              <input
                type="text"
                value={form.engine_number}
                onChange={(e) => set('engine_number')(e.target.value.toUpperCase())}
                placeholder="G4FL123456"
                className={inputCls}
              />
            </Field>
            <Field label="Chassis number" hint="VIN — as on the RC">
              <input
                type="text"
                value={form.chassis_number}
                onChange={(e) => set('chassis_number')(e.target.value.toUpperCase())}
                placeholder="MA3EYD32S00123456"
                className={inputCls}
              />
            </Field>
          </div>

          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Field label="Owners so far" hint="1 = first owner">
              <NumberInput value={form.owners_count} onChange={set('owners_count')} decimals={false} placeholder="1" />
            </Field>
            <Field label="RC">
              <Select value={form.rc_status} onChange={set('rc_status')} options={RC_OPTIONS} />
            </Field>
            {/* Plenty of cars arrive with the insurance lapsed. "Nil" is a
                real answer, not a box someone forgot. */}
            <Field label="Insurance valid till">
              <div className="flex gap-1.5">
                <input
                  type="date"
                  value={form.insurance_valid_till}
                  onChange={(e) => set('insurance_valid_till')(e.target.value)}
                  className={`${inputCls} min-w-0 flex-1`}
                />
                <button
                  type="button"
                  onClick={() => set('insurance_valid_till')('')}
                  title="No insurance on this car"
                  className={`shrink-0 rounded-xl border px-2.5 text-xs font-bold transition ${
                    form.insurance_valid_till
                      ? 'border-slate-200 text-slate-400 hover:border-rose-200 hover:text-rose-600'
                      : 'border-rose-200 bg-rose-50 text-rose-600'
                  }`}
                >
                  Nil
                </button>
              </div>
            </Field>
          </div>
        </div>

        {/* The money */}
        <div className="rounded-xl border border-violet-200 bg-violet-50/50 p-4">
          <p className="mb-3 text-xs font-bold uppercase tracking-widest text-violet-500">The money</p>

          {consignment ? (
            <>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label="Owner's name" required>
                  <input type="text" value={form.owner_name} onChange={(e) => set('owner_name')(e.target.value)} placeholder="Kunja" className={inputCls} />
                </Field>
                <Field label="Owner's phone">
                  <input type="text" value={form.owner_phone} onChange={(e) => set('owner_phone')(e.target.value)} placeholder="10-digit mobile number" className={inputCls} />
                </Field>
              </div>
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                <Field label="Owner's price" hint="What they must walk away with">
                  <NumberInput value={form.owner_price} onChange={set('owner_price')} placeholder="900000" />
                </Field>
                <Field label="Asking price" hint="What we offer it at">
                  <NumberInput value={form.asking_price} onChange={set('asking_price')} placeholder="940000" />
                </Field>
                <Field label="How is CAP paid?" required>
                  <Select value={form.commission_type} onChange={set('commission_type')} options={COMMISSION_TYPES} />
                </Field>
              </div>
              {form.commission_type !== 'margin' && (
                <div className="mt-3 sm:w-1/3">
                  <Field
                    label={form.commission_type === 'percent' ? 'Commission %' : 'Commission ₹'}
                    required
                  >
                    <NumberInput
                      value={form.commission_value}
                      onChange={set('commission_value')}
                      placeholder={form.commission_type === 'percent' ? '5' : '25000'}
                    />
                  </Field>
                </div>
              )}
              {form.commission_type === 'margin' && (
                <p className="mt-3 rounded-lg bg-white px-3 py-2 text-xs text-slate-500">
                  The owner gets their price. Everything the sale beats it by is CAP's — and if it
                  sells for less, CAP simply earns nothing on it.
                </p>
              )}
            </>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="What CAP paid" required hint="The purchase price">
                <NumberInput value={form.purchase_price} onChange={set('purchase_price')} placeholder="450000" />
              </Field>
              <Field label="Asking price" hint="What we offer it at">
                <NumberInput value={form.asking_price} onChange={set('asking_price')} placeholder="520000" />
              </Field>
            </div>
          )}
        </div>

        <Field label="Notes" hint="Anything worth knowing — service history, dents, who brought it in.">
          <textarea
            rows={2}
            value={form.notes}
            onChange={(e) => set('notes')(e.target.value)}
            className={inputCls}
          />
        </Field>
      </div>

      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={submit} loading={busy}>{editing ? 'Save changes' : 'Add car'}</Button>
      </div>
    </Modal>
  );
};

export default AddCarModal;
